import {
  Box,
  Card,
  Heading,
  HStack,
  Image,
  Skeleton,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LottieReact } from "@lottielab/lottie-player";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { REWARD_CATEGORY_METADATA } from "./constants";

import { Countdown } from "@/components/countdown";
import { RoundedBadge } from "@/components/rounded-badge";
import { SkeletonCircle } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useDailySpinOnchain } from "@/hooks/useDailySpinOnchain";
import { RewardCategory, RewardRecord } from "@/types";
import { isDailySpinDeprecated } from "@/utils";

dayjs.extend(utc);

export const DailySpinHistory = ({
  limit,
  data,
  localRecentSpin,
  isLoading,
}: {
  limit?: number;
  data: RewardRecord[];
  localRecentSpin: RewardRecord | null;
  isLoading: boolean;
}) => {
  const { hasSpunToday, isLoadingDailySpinStats, dailySpinStreak } =
    useDailySpinOnchain();
  const { isAuthenticated } = useAuth();

  if (isLoading || isLoadingDailySpinStats) {
    return (
      <VStack flex={1} alignSelf="stretch" justifyContent="space-between">
        <RewardCardSkeleton />
        <RewardCardSkeleton />
        <RewardCardSkeleton />
        <RewardCardSkeleton />
      </VStack>
    );
  }

  // show the localRecentSpin (placeholder for indexing data before it is fetched for a new spin) if:
  // (1) the localRecentSpin exists AND
  // (2) the history either does not exist or if it does exist the most recent history is not the same date as the localRecentSpin
  const showLocalRecentSpin =
    localRecentSpin &&
    (!data?.[0]?.dateStr || localRecentSpin.dateStr !== data?.[0]?.dateStr);

  // Only show "not yet spun" card when daily spin is active (not deprecated)
  const showNotYetSpun =
    !isDailySpinDeprecated() && !hasSpunToday && !isLoadingDailySpinStats;

  const hasAnySpins = data?.length > 0 || !!localRecentSpin;
  const isLoggedIn = isAuthenticated;

  return (
    <Box flex={1} position="relative" w="full" minH={[382, 382, 382, 382, 0]}>
      <VStack w="full" position="absolute" inset={0} h="full" overflow="scroll">
        {/* if the user hasn't spun yet today, show the "not yet spun" card */}
        {showNotYetSpun && isLoggedIn && (
          <DailySpinRecord
            rewardCategory={RewardCategory.NotYetSpun}
            rewardAmount={0}
            streak={dailySpinStreak + 1} // show the potential new streak if they spin today
            dateStr={""}
          />
        )}

        {!hasAnySpins && (
          <VStack
            rounded="16px"
            w="full"
            alignItems="center"
            justifyContent="center"
            h="full"
            bg="bg"
            overflow="scroll"
            textAlign="center"
            px={["20px", "20px", "20px", "60px"]}
            py={["100px", "120px", "120px", "100px"]}
            pb={["160px", "180px", "180px", "160px"]}
            position="relative"
          >
            <VStack gap={4} maxW={342}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
              >
                <path
                  d="M33.4152 19.602L15.9152 38.352C15.7297 38.55 15.4849 38.6822 15.2178 38.7288C14.9506 38.7753 14.6755 38.7338 14.434 38.6103C14.1925 38.4868 13.9977 38.2881 13.8791 38.0443C13.7604 37.8004 13.7243 37.5245 13.7761 37.2583L16.0668 25.8005L7.06206 22.4192C6.86867 22.3469 6.69622 22.2278 6.5601 22.0725C6.42398 21.9173 6.32844 21.7307 6.28201 21.5295C6.23558 21.3283 6.23972 21.1188 6.29404 20.9196C6.34836 20.7204 6.45118 20.5377 6.59331 20.388L24.0933 1.63798C24.2788 1.44006 24.5236 1.30784 24.7907 1.26125C25.0579 1.21467 25.333 1.25625 25.5745 1.37973C25.816 1.50321 26.0108 1.70188 26.1294 1.94576C26.2481 2.18964 26.2842 2.46551 26.2324 2.73173L23.9355 14.202L32.9402 17.5786C33.1322 17.6514 33.3032 17.7704 33.4383 17.925C33.5733 18.0797 33.6682 18.2652 33.7146 18.4652C33.7609 18.6652 33.7573 18.8736 33.704 19.0719C33.6506 19.2701 33.5493 19.4522 33.4089 19.602H33.4152Z"
                  fill="#989898"
                />
              </svg>
              <Heading
                fontSize="lg"
                fontWeight="500"
                lineHeight="24px"
                color="text.secondary"
              >
                Spin daily to earn rewards and track your progress here
              </Heading>
            </VStack>
            <Box position="absolute" bottom={10} left={0} right={0}>
              <LottieReact src="https://cdn.lottielab.com/l/E8nKC5Y75Qi9M6.json" />
            </Box>
          </VStack>
        )}
        {showLocalRecentSpin && (
          <DailySpinRecord
            rewardCategory={localRecentSpin.rewardCategory}
            rewardAmount={localRecentSpin.rewardAmount}
            streak={dailySpinStreak} // this is from onchain, should update immediately on new spin
            dateStr={localRecentSpin.dateStr}
          />
        )}
        {/* map through the history and show each spin */}
        {data
          ?.slice(0, limit ?? data.length)
          ?.map(
            ({
              rewardCategory,
              rewardAmount,
              streak,
              dateStr,
            }: RewardRecord) => (
              <DailySpinRecord
                key={dateStr + rewardCategory + rewardAmount + streak}
                rewardCategory={rewardCategory}
                rewardAmount={rewardAmount}
                streak={streak}
                dateStr={dateStr}
              />
            ),
          )}
      </VStack>
    </Box>
  );
};

const DailySpinRecord = ({
  rewardCategory,
  rewardAmount,
  streak,
  dateStr,
}: RewardRecord) => {
  const { icon, rewardCurrency, rewardShorthand } =
    REWARD_CATEGORY_METADATA[rewardCategory as RewardCategory];

  return (
    <Card.Root
      w="full"
      bgColor={rewardCategory === RewardCategory.NotYetSpun ? "#EAF0E9" : "bg"}
      borderRadius="2xl"
    >
      <Card.Body p={4}>
        <HStack>
          <Image src={icon} boxSize={10} />
          <Box ml={2}>
            <Heading size="lg" fontWeight="500">
              {rewardCurrency(BigInt(rewardAmount))}
            </Heading>
            <Box fontSize="md" fontWeight="500" color="fg.muted">
              {/* if the user hasn't spun yet today, show the countdown */}
              {rewardCategory === RewardCategory.NotYetSpun ? (
                <HStack>
                  <Text>Time remaining:</Text>
                  <Countdown w={75} fontWeight="500" />
                </HStack>
              ) : (
                dateStr.replace(/-/g, ".")
              )}
            </Box>
          </Box>
          <Spacer flex={1} />
          {rewardCategory !== "Nothing" &&
            rewardCategory !== RewardCategory.NotYetSpun && (
              <RoundedBadge
                bgColor="bg"
                h={10}
                borderWidth="thin"
                borderColor="border"
              >
                + {rewardAmount} {rewardShorthand}
              </RoundedBadge>
            )}
          {rewardCategory !== RewardCategory.NotYetSpun ? (
            <RoundedBadge
              bgColor="bg"
              h={10}
              borderWidth="thin"
              borderColor="border"
            >
              <Image
                src="/assets/daily-spin/lightning-bolt-streak.svg"
                boxSize={4}
              />
              {streak}
            </RoundedBadge>
          ) : (
            // if a user hasnt spun today, show the gray streak icon and the diff background shade
            <RoundedBadge
              bgColor="white"
              h={10}
              borderWidth="thin"
              borderColor="border"
            >
              <Image
                src="/assets/daily-spin/lightning-bolt-streak-gray.svg"
                boxSize={4}
              />
              {streak}
            </RoundedBadge>
          )}
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};

const RewardCardSkeleton = () => (
  <Card.Root w="full" bg="bg">
    <Card.Body>
      <HStack gap={4} align="center">
        <SkeletonCircle size="10" />

        <Box flex="1">
          <Skeleton height="20px" width="100px" mb={2} />
          <Skeleton height="14px" width="80px" />
        </Box>

        <HStack gap={2} hideBelow="md">
          <Skeleton height="24px" width="60px" borderRadius="full" />
          <Skeleton height="24px" width="50px" borderRadius="full" />
        </HStack>
      </HStack>
    </Card.Body>
  </Card.Root>
);
